import {
  parseEvent,
  COMPANION_REPLY_DELTA_MAX_CODE_POINTS,
  COMPANION_TURN_TEXT_MAX_CODE_POINTS,
  type CompanionBridgeEvent,
  type CompanionMate,
  type CompanionTurn,
  type PersonaKey,
} from "@m8t-stack/companion-bridge-contract";
import { stat } from "node:fs/promises";
import { getFoundryToken } from "./auth.js";
import { getConfigPath } from "./config.js";
import {
  resolveGatewayContext,
  type GatewayContext,
} from "./gateway-context.js";

const RESPONSE_MAX_BYTES = 1_000_000;
const REQUEST_DEADLINE_MS = 120_000;
const OPAQUE_ID = /^[A-Za-z0-9_-]{1,256}$/u;
const AGENT_NAME_MAX_LENGTH = 256;

export interface CompanionChatDeps {
  readonly fetch?: typeof fetch;
  readonly resolveGatewayContext?: typeof resolveGatewayContext;
  readonly getFoundryToken?: typeof getFoundryToken;
  readonly now?: () => number;
  readonly deadlineMs?: number;
  readonly installEpoch?: () => Promise<number | null>;
  /**
   * The agent roster and conversation binding are re-resolved on every
   * request by default. A serving bridge session injects caching wrappers
   * here so a warm request skips those round trips.
   */
  readonly fetchAgents?: typeof fetchAgents;
  readonly resolveConversationId?: typeof resolveConversationId;
}

export interface CompanionSend {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly draftRevision: number;
  readonly text: string;
}

export interface CompanionHistoryRequest {
  readonly requestId: string;
  readonly personaKey: PersonaKey;
  readonly limit: number;
}

type EventSink = (event: CompanionBridgeEvent) => void;
type FailureReason = Extract<
  CompanionBridgeEvent,
  { type: "failed" }
>["reason"];

class PredispatchFailure extends Error {
  constructor(readonly reason: FailureReason) {
    super("companion bridge pre-dispatch failure");
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function isAgentName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= AGENT_NAME_MAX_LENGTH &&
    !hasControlCharacter(value)
  );
}

function authenticatedHeaders(token: string): Record<string, string> {
  return {
    accept: "application/json",
    authorization: `Bearer ${token}`,
  };
}

function gatewayOrigin(gateway: GatewayContext): string {
  let url: URL;
  try {
    url = new URL(gateway.gatewayUrl);
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return url.origin;
}

async function readBoundedText(
  response: Response,
  maxBytes = RESPONSE_MAX_BYTES,
): Promise<string> {
  const declared = response.headers.get("content-length");
  if (declared !== null && Number(declared) > maxBytes) {
    throw new Error("bounded response rejected");
  }
  if (!response.body) throw new Error("response body unavailable");

  const reader = (
    response.body as ReadableStream<Uint8Array>
  ).getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let total = 0;
  let text = "";
  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    total += part.value.byteLength;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      throw new Error("bounded response rejected");
    }
    text += decoder.decode(part.value, { stream: true });
  }
  text += decoder.decode();
  return text;
}

async function readJsonEnvelope(response: Response): Promise<unknown> {
  if (!response.ok) {
    const reason =
      response.status === 401 || response.status === 403
        ? "not-authorized"
        : response.status === 409
          ? "busy"
          : "not-connected";
    throw new PredispatchFailure(reason);
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    throw new PredispatchFailure("not-connected");
  }

  let envelope: unknown;
  try {
    envelope = JSON.parse(await readBoundedText(response));
  } catch (error) {
    if (error instanceof PredispatchFailure) throw error;
    throw new PredispatchFailure("not-connected");
  }
  if (!isRecord(envelope) || envelope.ok !== true || !("data" in envelope)) {
    throw new PredispatchFailure("not-connected");
  }
  return envelope.data;
}

function decodeAgents(data: unknown): readonly Record<string, unknown>[] {
  if (!isRecord(data) || !Array.isArray(data.agents)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return data.agents.filter(isRecord);
}

function resolveAgentName(
  agents: readonly Record<string, unknown>[],
  personaKey: PersonaKey,
): string {
  const matches = agents.filter(
    (agent) =>
      agent.personaKey === personaKey && isAgentName(agent.name),
  );
  if (matches.length !== 1) {
    throw new PredispatchFailure("mate-unavailable");
  }
  const match = matches[0];
  if (!isAgentName(match.name)) {
    throw new PredispatchFailure("mate-unavailable");
  }
  return match.name;
}

function decodeConversationId(data: unknown): string {
  if (
    !isRecord(data) ||
    typeof data.id !== "string" ||
    !OPAQUE_ID.test(data.id)
  ) {
    throw new PredispatchFailure("not-connected");
  }
  return data.id;
}

function decodeLastMessageId(data: unknown): string | undefined {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const messages = data.messages as readonly unknown[];
  const last: unknown = messages.at(-1);
  if (
    !isRecord(last) ||
    typeof last.id !== "string" ||
    !OPAQUE_ID.test(last.id)
  ) {
    return undefined;
  }
  return last.id;
}

export async function resolveConversationId(
  fetchImpl: typeof fetch,
  origin: string,
  headers: Record<string, string>,
  agentName: string,
  signal: AbortSignal,
): Promise<string> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/api/conversations`, {
      method: "POST",
      headers: { ...headers, "content-type": "application/json" },
      body: JSON.stringify({ agentName }),
      signal,
    });
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return decodeConversationId(await readJsonEnvelope(response));
}

async function fetchConversationMessages(
  fetchImpl: typeof fetch,
  origin: string,
  headers: Record<string, string>,
  conversationId: string,
  signal: AbortSignal,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetchImpl(
      `${origin}/api/conversations/${encodeURIComponent(conversationId)}/messages`,
      { headers, signal },
    );
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return readJsonEnvelope(response);
}

/** Timestamps sane enough to format: positive epoch seconds this side of 5138. */
const EPOCH_SECONDS_LIMIT = 100_000_000_000;

/** Line breaks and tabs stay; every other control character is dropped. */
function sanitizeMessageText(value: string): string {
  let out = "";
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    const forbidden =
      code === 0x7f ||
      (code <= 0x1f && code !== 0x09 && code !== 0x0a && code !== 0x0d);
    if (!forbidden) out += character;
  }
  return out;
}

function boundCodePoints(value: string, maxCodePoints: number): string {
  let count = 0;
  let out = "";
  for (const character of value) {
    if (count >= maxCodePoints) return out;
    out += character;
    count += 1;
  }
  return value;
}

/**
 * The turns the panel shows, decoded defensively: only user and mate text
 * messages, cleaned to what the bridge contract will carry, capped to the
 * newest `limit`. An entry that cannot be rendered is skipped, never fatal.
 */
function decodeTurns(data: unknown, limit: number): CompanionTurn[] {
  if (!isRecord(data) || !Array.isArray(data.messages)) {
    throw new PredispatchFailure("not-connected");
  }
  const turns: CompanionTurn[] = [];
  for (const entry of data.messages as readonly unknown[]) {
    if (!isRecord(entry)) continue;
    if (entry.role !== "user" && entry.role !== "assistant") continue;
    if (typeof entry.id !== "string" || !OPAQUE_ID.test(entry.id)) continue;
    if (typeof entry.content !== "string") continue;
    const text = boundCodePoints(
      sanitizeMessageText(entry.content),
      COMPANION_TURN_TEXT_MAX_CODE_POINTS,
    );
    if (text.length === 0) continue;
    const at =
      typeof entry.createdAt === "number" &&
      Number.isFinite(entry.createdAt) &&
      entry.createdAt > 0 &&
      entry.createdAt < EPOCH_SECONDS_LIMIT
        ? new Date(Math.round(entry.createdAt) * 1000).toISOString()
        : null;
    turns.push({
      id: entry.id,
      role: entry.role === "user" ? "user" : "mate",
      text,
      at,
    });
  }
  return turns.slice(-limit);
}

function mateRoute(
  personaKey: PersonaKey,
  desktopAfter?: string,
): string {
  const marker =
    desktopAfter === undefined ? "" : `&desktopAfter=${desktopAfter}`;
  return `/mates/${personaKey}?desktopPending=1${marker}`;
}

function emitFailure(
  sink: EventSink,
  reason: FailureReason,
  requestId?: string,
): CompanionBridgeEvent {
  const event = parseEvent(
    requestId === undefined
      ? { type: "failed", reason }
      : { type: "failed", requestId, reason },
  );
  sink(event);
  return event;
}

function classifyUnknownPredispatch(error: unknown): FailureReason {
  return error instanceof PredispatchFailure
    ? error.reason
    : "not-connected";
}

/**
 * The gateway accepts both its own API audience and the Foundry audience, but
 * an interactive caller's bearer is forwarded to Foundry unchanged. A token
 * minted for `api://<gatewayClientId>` therefore reaches Foundry with the wrong
 * audience and is rejected, so every companion request carries the Foundry
 * audience — the same one the web product's sign-in asks for.
 */
async function acquireContext(
  deps: CompanionChatDeps,
): Promise<{ token: string; origin: string }> {
  let gateway: GatewayContext;
  let token: string;
  try {
    gateway = await (deps.resolveGatewayContext ?? resolveGatewayContext)({
      interactive: false,
    });
    token = await (deps.getFoundryToken ?? getFoundryToken)();
  } catch {
    throw new PredispatchFailure("not-authorized");
  }
  return { token, origin: gatewayOrigin(gateway) };
}

export async function fetchAgents(
  fetchImpl: typeof fetch,
  origin: string,
  token: string,
  signal: AbortSignal,
): Promise<readonly Record<string, unknown>[]> {
  let response: Response;
  try {
    response = await fetchImpl(`${origin}/api/agents`, {
      headers: authenticatedHeaders(token),
      signal,
    });
  } catch {
    throw new PredispatchFailure("not-connected");
  }
  return decodeAgents(await readJsonEnvelope(response));
}

function createDeadline(
  deps: CompanionChatDeps,
): { controller: AbortController; timer: ReturnType<typeof setTimeout> } {
  const controller = new AbortController();
  const timer = setTimeout(
    () => {
      controller.abort();
    },
    deps.deadlineMs ?? REQUEST_DEADLINE_MS,
  );
  return { controller, timer };
}

/**
 * When m8t was installed on this workstation, taken from the earliest
 * timestamp on the CLI configuration file. The mates have been reachable since
 * the platform installation that created it, so this is the honest lower bound
 * for "Active for …". Returns null when the file cannot be read.
 */
async function readInstallEpoch(): Promise<number | null> {
  try {
    const stats = await stat(getConfigPath());
    const candidates = [stats.birthtimeMs, stats.mtimeMs].filter(
      (value) => Number.isFinite(value) && value > 0,
    );
    if (candidates.length === 0) return null;
    return Math.min(...candidates);
  } catch {
    return null;
  }
}

export async function rosterCompanions(
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  try {
    const { token, origin } = await acquireContext(deps);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      deps.fetch ?? fetch,
      origin,
      token,
      controller.signal,
    );
    const now = (deps.now ?? Date.now)();
    const installed = await (deps.installEpoch ?? readInstallEpoch)();
    // Never claim an age we cannot stand behind: an unreadable or
    // future-dated install stamp falls back to this moment.
    const activeSince = new Date(
      installed === null || installed > now ? now : installed,
    ).toISOString();
    const mates: readonly CompanionMate[] = [
      {
        personaKey: "startup-advisor",
        agentName: resolveAgentName(agents, "startup-advisor"),
        displayName: "Stacey",
        portraitKey: "stacey",
        activeSince,
      },
      {
        personaKey: "azure-advisor",
        agentName: resolveAgentName(agents, "azure-advisor"),
        displayName: "Azzy",
        portraitKey: "azzy",
        activeSince,
      },
    ];
    const event = parseEvent({ type: "roster", origin, mates });
    sink(event);
    return event;
  } catch (error) {
    return emitFailure(sink, classifyUnknownPredispatch(error));
  } finally {
    clearTimeout(timer);
  }
}

async function drainAcceptedSse(response: Response): Promise<void> {
  const text = await readBoundedText(response);
  const frames = text.split(/\r?\n\r?\n/u);
  let sawDone = false;

  for (const frame of frames) {
    if (frame.trim().length === 0) continue;
    const dataLines = frame
      .split(/\r?\n/u)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) continue;
    const data = dataLines.join("\n");
    if (sawDone) throw new Error("data after terminal stream marker");
    if (data === "[DONE]") {
      sawDone = true;
      continue;
    }
    const parsed = JSON.parse(data) as unknown;
    if (
      !isRecord(parsed) ||
      typeof parsed.type !== "string" ||
      parsed.type === "error"
    ) {
      throw new Error("invalid stream event");
    }
  }

  if (!sawDone) throw new Error("stream ended without terminal marker");
}

/** Coalesce deltas so event volume stays sane without the stream feeling dead. */
const REPLY_FLUSH_MIN_LENGTH = 64;
const REPLY_FLUSH_MS = 150;

function emitBoundedText(text: string, emit: (slice: string) => void): void {
  let slice = "";
  let count = 0;
  for (const character of text) {
    slice += character;
    count += 1;
    if (count === COMPANION_REPLY_DELTA_MAX_CODE_POINTS) {
      emit(slice);
      slice = "";
      count = 0;
    }
  }
  if (slice.length > 0) emit(slice);
}

/**
 * The same accepted-stream discipline as the draining reader, applied
 * incrementally: frames are parsed as they arrive so the text deltas can be
 * forwarded live rather than after the whole reply has landed.
 */
async function streamAcceptedSse(
  response: Response,
  now: () => number,
  onText: (text: string) => void,
): Promise<void> {
  if (!response.body) throw new Error("response body unavailable");
  const reader = (
    response.body as ReadableStream<Uint8Array>
  ).getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let buffered = "";
  let total = 0;
  const terminal = { sawDone: false };
  let pending = "";
  let lastFlush = now();

  const flush = (): void => {
    const text = sanitizeMessageText(pending);
    pending = "";
    lastFlush = now();
    if (text.length > 0) emitBoundedText(text, onText);
  };

  const handleFrame = (frame: string): void => {
    if (frame.trim().length === 0) return;
    const dataLines = frame
      .split(/\r?\n/u)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) return;
    const data = dataLines.join("\n");
    if (terminal.sawDone) throw new Error("data after terminal stream marker");
    if (data === "[DONE]") {
      terminal.sawDone = true;
      return;
    }
    const parsed = JSON.parse(data) as unknown;
    if (
      !isRecord(parsed) ||
      typeof parsed.type !== "string" ||
      parsed.type === "error"
    ) {
      throw new Error("invalid stream event");
    }
    if (
      parsed.type === "text-delta" &&
      typeof parsed.delta === "string" &&
      parsed.delta.length > 0
    ) {
      pending += parsed.delta;
      if (
        pending.length >= REPLY_FLUSH_MIN_LENGTH ||
        now() - lastFlush >= REPLY_FLUSH_MS
      ) {
        flush();
      }
    }
  };

  const drainFrames = (): void => {
    for (;;) {
      const boundary = /\r?\n\r?\n/u.exec(buffered);
      if (!boundary) return;
      const frame = buffered.slice(0, boundary.index);
      buffered = buffered.slice(boundary.index + boundary[0].length);
      handleFrame(frame);
    }
  };

  for (;;) {
    const part = await reader.read();
    if (part.done) break;
    total += part.value.byteLength;
    if (total > RESPONSE_MAX_BYTES) {
      await reader.cancel().catch(() => undefined);
      throw new Error("bounded response rejected");
    }
    buffered += decoder.decode(part.value, { stream: true });
    drainFrames();
    if (terminal.sawDone) {
      // The stream has said everything it will say; a connection a proxy
      // holds open past this point must not stall a finished reply.
      await reader.cancel().catch(() => undefined);
      break;
    }
  }
  if (!terminal.sawDone) {
    buffered += decoder.decode();
    drainFrames();
    if (buffered.trim().length > 0) handleFrame(buffered);
  }
  flush();
  if (!terminal.sawDone) {
    throw new Error("stream ended without terminal marker");
  }
}

async function dispatchConversation(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps,
  consume: (response: Response) => Promise<void>,
  readDesktopMarker: boolean,
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  const fetchImpl = deps.fetch ?? fetch;
  let route: string | undefined;
  let dispatched = false;
  let accepted = false;

  try {
    const { token, origin } = await acquireContext(deps);
    const headers = authenticatedHeaders(token);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      fetchImpl,
      origin,
      token,
      controller.signal,
    );
    const agentName = resolveAgentName(agents, request.personaKey);
    const conversationId = await (deps.resolveConversationId ?? resolveConversationId)(
      fetchImpl,
      origin,
      headers,
      agentName,
      controller.signal,
    );
    // The marker anchors the browser handoff's pending-turn display. A
    // conversing panel never opens the browser, so it skips this read and a
    // whole authenticated round trip with it.
    const desktopAfter = readDesktopMarker
      ? decodeLastMessageId(
          await fetchConversationMessages(
            fetchImpl,
            origin,
            headers,
            conversationId,
            controller.signal,
          ),
        )
      : undefined;
    route = mateRoute(request.personaKey, desktopAfter);

    const dispatching = parseEvent({
      type: "dispatching",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(dispatching);
    dispatched = true;

    let chatResponse: Response;
    try {
      chatResponse = await fetchImpl(`${origin}/api/chat`, {
        method: "POST",
        headers: { ...headers, "content-type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", content: request.text }],
          agentName,
          conversationId,
        }),
        signal: controller.signal,
      });
    } catch {
      throw new Error("chat dispatch outcome unavailable");
    }

    if (
      !chatResponse.ok ||
      !chatResponse.body ||
      !(chatResponse.headers.get("content-type") ?? "")
        .toLowerCase()
        .includes("text/event-stream")
    ) {
      throw new Error("chat response not accepted");
    }

    const acceptedEvent = parseEvent({
      type: "accepted",
      requestId: request.requestId,
      personaKey: request.personaKey,
      route,
    });
    sink(acceptedEvent);
    accepted = true;

    await consume(chatResponse);
    const completed = parseEvent({
      type: "completed",
      requestId: request.requestId,
    });
    sink(completed);
    return completed;
  } catch (error) {
    if (!dispatched) {
      return emitFailure(
        sink,
        classifyUnknownPredispatch(error),
        request.requestId,
      );
    }
    const indeterminate = parseEvent({
      type: "indeterminate",
      requestId: request.requestId,
      stage: accepted ? "after-accept" : "dispatch",
      route: route ?? mateRoute(request.personaKey),
    });
    sink(indeterminate);
    return indeterminate;
  } finally {
    clearTimeout(timer);
  }
}

export async function sendCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return dispatchConversation(request, sink, deps, drainAcceptedSse, true);
}

/**
 * `send`, with the mate's answer carried back: the accepted stream's text
 * deltas are forwarded as reply-delta events instead of being discarded.
 */
export async function converseCompanionMessage(
  request: CompanionSend,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  return dispatchConversation(
    request,
    sink,
    deps,
    (response) =>
      streamAcceptedSse(response, deps.now ?? Date.now, (text) => {
        sink(
          parseEvent({
            type: "reply-delta",
            requestId: request.requestId,
            text,
          }),
        );
      }),
    false,
  );
}

/**
 * The newest turns of the mate's canonical conversation, one turn event per
 * message, oldest first, closed by history-end.
 */
export async function historyCompanionMessages(
  request: CompanionHistoryRequest,
  sink: EventSink,
  deps: CompanionChatDeps = {},
): Promise<CompanionBridgeEvent> {
  const { controller, timer } = createDeadline(deps);
  const fetchImpl = deps.fetch ?? fetch;
  try {
    const { token, origin } = await acquireContext(deps);
    const headers = authenticatedHeaders(token);
    const agents = await (deps.fetchAgents ?? fetchAgents)(
      fetchImpl,
      origin,
      token,
      controller.signal,
    );
    const agentName = resolveAgentName(agents, request.personaKey);
    const conversationId = await (deps.resolveConversationId ?? resolveConversationId)(
      fetchImpl,
      origin,
      headers,
      agentName,
      controller.signal,
    );
    const turns = decodeTurns(
      await fetchConversationMessages(
        fetchImpl,
        origin,
        headers,
        conversationId,
        controller.signal,
      ),
      request.limit,
    );
    for (const turn of turns) {
      sink(parseEvent({ type: "turn", requestId: request.requestId, turn }));
    }
    const end = parseEvent({
      type: "history-end",
      requestId: request.requestId,
      count: turns.length,
    });
    sink(end);
    return end;
  } catch (error) {
    return emitFailure(
      sink,
      classifyUnknownPredispatch(error),
      request.requestId,
    );
  } finally {
    clearTimeout(timer);
  }
}
